import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import SEOHead from '../SEOHead';
import './AdminLayout.css';

const AdminLayout = ({ children, activeTab, setActiveTab, counts = {} }) => {
  const { isAuthenticated, loading, logout, user } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [currentTime, setCurrentTime] = useState('');

  // Clock updating in IST
  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      setCurrentTime(
        now.toLocaleString('en-IN', {
          timeZone: 'Asia/Kolkata',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true,
        }) + ' IST'
      );
    };
    updateTime();
    const interval = setInterval(updateTime, 1000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <div className="admin-loading-screen">
        <div className="spinner-border text-orange" role="status">
          <span className="visually-hidden">Validating session...</span>
        </div>
        <p className="mt-3 text-muted fw-semibold">Loading Doctor & Management Portal...</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  const navItems = [
    { id: 'overview', label: 'Dashboard Overview', icon: 'fa-gauge-high' },
    {
      id: 'patients',
      label: 'Patient Directory',
      icon: 'fa-hospital-user',
      badge: counts.totalPatients || null,
      badgeColor: 'badge-teal',
    },
    {
      id: 'appointments',
      label: 'Appointments Desk',
      icon: 'fa-calendar-check',
      badge: counts.pendingAppointments || null,
      badgeColor: 'badge-amber',
    },
    {
      id: 'contacts',
      label: 'Patient Inquiries',
      icon: 'fa-envelope-open-text',
      badge: counts.unreadContacts || null,
      badgeColor: 'badge-orange',
    },
    { id: 'articles', label: 'Articles & Blogs', icon: 'fa-newspaper' },
    { id: 'gallery', label: 'Surgical Gallery', icon: 'fa-photo-film' },
    { id: 'reviews', label: 'Patient Reviews', icon: 'fa-star' },
    {
      id: 'trash',
      label: 'Recycle Bin',
      icon: 'fa-trash-can',
      badge: counts.totalTrash || null,
      badgeColor: 'badge-red',
    },
  ];

  return (
    <div className="admin-app-container">
      <SEOHead title="Doctor & Management Operations Portal" noindex={true} />

      {/* Mobile Drawer Overlay */}
      {isSidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* Sidebar Navigation */}
      <aside className={`admin-sidebar ${isSidebarOpen ? 'open' : ''}`}>
        <div className="sidebar-brand-wrapper">
          <div className="sidebar-brand d-flex align-items-center gap-3">
            <div className="brand-icon-box">
              <img
                src="/images/logo_new.png"
                alt="Logo"
                className="sidebar-logo-img"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
            <div>
              <h5 className="mb-0 brand-name text-white">Dr. Sunil Apsingi</h5>
              <span className="brand-dept">Ortho Management</span>
            </div>
          </div>
          <button
            className="btn btn-close-sidebar d-lg-none"
            onClick={() => setIsSidebarOpen(false)}
          >
            <i className="fa-solid fa-xmark text-white"></i>
          </button>
        </div>

        <div className="sidebar-body">
          <div className="nav-group-title">CLINICAL OPERATIONS</div>
          <ul className="sidebar-nav-list">
            {navItems.map((item) => (
              <li key={item.id}>
                <button
                  type="button"
                  className={`nav-tab-btn ${activeTab === item.id ? 'active' : ''}`}
                  onClick={() => {
                    setActiveTab(item.id);
                    setIsSidebarOpen(false);
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <i className={`fa-solid ${item.icon} nav-icon`}></i>
                    <span className="nav-label">{item.label}</span>
                  </div>
                  {item.badge ? (
                    <span className={`count-badge ${item.badgeColor}`}>
                      {item.badge}
                    </span>
                  ) : null}
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* Sidebar Footer */}
        <div className="sidebar-footer">
          <div className="user-profile-badge d-flex align-items-center gap-3 mb-3">
            <div className="user-avatar-circle">
              <i className="fa-solid fa-user-doctor text-white"></i>
            </div>
            <div className="flex-grow-1 overflow-hidden">
              <strong className="d-block text-white text-truncate small">
                {user?.username || 'Staff'}
              </strong>
              <small className="text-white-50 text-truncate d-block" style={{ fontSize: '0.72rem' }}>
                {user?.is_superuser ? 'Chief Administrator' : 'Clinical Manager'}
              </small>
            </div>
          </div>

          <button
            type="button"
            className="btn btn-sidebar-logout w-100 d-flex align-items-center justify-content-center gap-2"
            onClick={logout}
          >
            <i className="fa-solid fa-arrow-right-from-bracket"></i>
            <span>Log Out</span>
          </button>
        </div>
      </aside>

      {/* Main Workspace Area */}
      <div className="admin-main-viewport">
        {/* Topbar Header */}
        <header className="admin-topbar">
          <div className="d-flex align-items-center gap-3">
            <button
              className="btn btn-toggle-menu d-lg-none"
              onClick={() => setIsSidebarOpen(true)}
              aria-label="Toggle menu"
            >
              <i className="fa-solid fa-bars fs-5 text-navy"></i>
            </button>
            <div className="topbar-title-block">
              <h4 className="topbar-page-heading mb-0">
                {navItems.find((n) => n.id === activeTab)?.label || 'Operations Desk'}
              </h4>
            </div>
          </div>

          <div className="d-flex align-items-center gap-3">
            {/* Real-time Clock */}
            <div className="topbar-time-pill d-none d-md-flex align-items-center gap-2">
              <i className="fa-regular fa-clock text-orange"></i>
              <span>{currentTime}</span>
            </div>

            {/* Link to public website */}
            <a
              href="/"
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-outline-public d-flex align-items-center gap-2"
              title="Open public website in a new tab"
            >
              <i className="fa-solid fa-arrow-up-right-from-square"></i>
              <span className="d-none d-sm-inline">Live Website</span>
            </a>
          </div>
        </header>

        {/* Dynamic Tab Content Area */}
        <main className="admin-content-canvas">{children}</main>
      </div>
    </div>
  );
};

export default AdminLayout;
